import React, { useEffect, createElement, Component } from 'react';
/**
 * LeadTrackingScripts
 *
 * Injects the LeadID (Jornaya) and TrustedForm tracking scripts into the
 * document, and renders the two hidden inputs that those scripts populate:
 *
 *   - input[name="leadid_token"]        ← LeadID token
 *   - input[name="xxTrustedFormCertUrl"] ← TrustedForm cert URL
 *
 * Mount this ONCE at the top of the app. The scripts are guarded so they only
 * load a single time per page-view even if the component re-mounts.
 *
 * Lead form submit handlers should call `readLeadTrackingValues()` right
 * before posting to the lead API and merge the returned values into the
 * payload (`leadid_token` + `xxTrustedFormCertUrl`).
 */
declare global {
  interface Window {
    myCallbackFunction?: (token: string) => void;
    __leadTrackingInjected?: boolean;
    __mgidSensorInjected?: boolean;
    _mgq?: any[];
    MgSensorData?: any[];
  }
}
const URL_PARAMS_STORAGE_KEY = 'mp_url_params';
/**
 * Fire a Mgid Sensor event. Safe to call anywhere — it lazily initializes the
 * `_mgq` queue so events fired before the sensor script finishes loading are
 * still captured and replayed once it's ready.
 *
 *   invokeMgSensor('submission')    // form submitted
 *   invokeMgSensor('initial_click') // first interaction with the matcher
 */
export function invokeMgSensor(event: string): void {
  if (typeof window === 'undefined') return;
  window._mgq = window._mgq || [];
  window._mgq.push(['MgSensorInvoke', event]);
}
export function LeadTrackingScripts() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.__leadTrackingInjected) return;
    window.__leadTrackingInjected = true;
    // Define LeadID callback BEFORE the script loads so it can write into
    // the hidden input as soon as the token is generated.
    window.myCallbackFunction = function (token: string) {
      const field = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
      if (field) field.setAttribute('value', token);
    };
    // ── LeadID (Jornaya) ────────────────────────────────────────────────
    const leadIdAnchor = document.createElement('script');
    leadIdAnchor.id = 'LeadiDscript';
    leadIdAnchor.type = 'text/javascript';
    document.head.appendChild(leadIdAnchor);
    const leadIdLoader = document.createElement('script');
    leadIdLoader.id = 'LeadiDscript_campaign';
    leadIdLoader.type = 'text/javascript';
    leadIdLoader.async = true;
    leadIdLoader.src = '//create.lidstatic.com/campaign/d50a3557-9854-7b8f-792c-527750401f5f.js?snippet_version=2&callback=myCallbackFunction';
    leadIdAnchor.parentNode?.insertBefore(leadIdLoader, leadIdAnchor);
    // ── TrustedForm ─────────────────────────────────────────────────────
    const tfField = 'xxTrustedFormCertUrl';
    const tfProvideReferrer = false;
    const tf = document.createElement('script');
    tf.type = 'text/javascript';
    tf.async = true;
    tf.src = 'http' + ('https:' === document.location.protocol ? 's' : '') + '://api.trustedform.com/trustedform.js?provide_referrer=' + encodeURIComponent(String(tfProvideReferrer)) + '&field=' + encodeURIComponent(tfField) + '&l=' + new Date().getTime() + Math.random();
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(tf, firstScript);
    // ── Mgid Sensor (pixel) ─────────────────────────────────────────────
    if (!window.__mgidSensorInjected) {
      window.__mgidSensorInjected = true;
      window.MgSensorData = window.MgSensorData || [];
      window.MgSensorData.push({
        cid: 976938,
        project: 'a.mgid.com'
      });
      const mgidHost = 'a.mgid.com';
      const mgidAnchor = document.getElementsByTagName('script')[0];
      const mgidScript = document.createElement('script');
      mgidScript.type = 'text/javascript';
      mgidScript.async = true;
      const dt = !Date.now ? new Date().valueOf() : Date.now();
      mgidScript.src = 'https://' + mgidHost + '/mgsensor.js?d=' + dt;
      mgidAnchor?.parentNode?.insertBefore(mgidScript, mgidAnchor);
    }
    // ── URL Tracking Params (first-touch capture) ───────────────────────
    try {
      const search = window.location.search;
      if (search) {
        const incoming = new URLSearchParams(search);
        const stored: Record<string, string> = (() => {
          try {
            const raw = window.sessionStorage.getItem(URL_PARAMS_STORAGE_KEY);
            return raw ? JSON.parse(raw) : {};
          } catch {
            return {};
          }
        })();
        let mutated = false;
        incoming.forEach((value, key) => {
          // First-touch: only set if we don't already have a value for this key
          if (!(key in stored) && value) {
            stored[key] = value;
            mutated = true;
          }
        });
        if (mutated) {
          window.sessionStorage.setItem(URL_PARAMS_STORAGE_KEY, JSON.stringify(stored));
        }
      }
    } catch {
      // sessionStorage may be unavailable (private mode, etc.) — fail silent
    }
    // ── Diagnostics: log tracking state at intervals after mount ───────
    // (Helps verify whether TrustedForm + LeadID actually populate the
    // hidden inputs on the current domain. Open DevTools console.)
    try {
      const logTrackingState = (label: string) => {
        const certNodes = document.querySelectorAll<HTMLInputElement>('input[name="xxTrustedFormCertUrl"]');
        const leadIdNodes = document.querySelectorAll<HTMLInputElement>('input[name="leadid_token"]');
        const tfScript = Array.from(document.getElementsByTagName('script')).find(s => (s.src || '').includes('trustedform.js'));
        const lidScript = Array.from(document.getElementsByTagName('script')).find(s => (s.src || '').includes('lidstatic.com'));
        // eslint-disable-next-line no-console
        console.groupCollapsed(`[LeadTracking diagnostic ${label}]`);
        // eslint-disable-next-line no-console
        console.log('trustedform.js script present:', !!tfScript, tfScript?.src);
        // eslint-disable-next-line no-console
        console.log('lidstatic.com script present:', !!lidScript, lidScript?.src);
        // eslint-disable-next-line no-console
        console.log(`xxTrustedFormCertUrl inputs found: ${certNodes.length}`, Array.from(certNodes).map(n => ({
          value: n.value,
          inForm: !!n.closest('form')
        })));
        // eslint-disable-next-line no-console
        console.log(`leadid_token inputs found: ${leadIdNodes.length}`, Array.from(leadIdNodes).map(n => ({
          value: n.value,
          inForm: !!n.closest('form')
        })));
        // eslint-disable-next-line no-console
        console.groupEnd();
      };
      setTimeout(() => logTrackingState('@ 2s'), 2000);
      setTimeout(() => logTrackingState('@ 5s'), 5000);
      setTimeout(() => logTrackingState('@ 10s'), 10000);
    } catch {
      // never let diagnostics break the app
    }
  }, []);
  // Hidden inputs the scripts write to. Kept fixed-position off-screen so
  // they never affect layout but stay in the DOM for the scripts to find.
  return <div aria-hidden="true" style={{
    position: 'absolute',
    width: 0,
    height: 0,
    overflow: 'hidden',
    pointerEvents: 'none'
  }} data-id="element-2047">
      <input type="hidden" name="leadid_token" id="leadid_token" value="" data-id="element-2048" />
      {/*
              NOTE: We deliberately do NOT render an xxTrustedFormCertUrl input
              here. TrustedForm's script automatically injects its own hidden
              input named xxTrustedFormCertUrl into every <form> on the page once
              it generates a cert. Rendering an empty one here would shadow the
              real (populated) one in `document.querySelector` lookups, and the
              cert would never make it into the webhook payload.
            */}
      <noscript data-id="element-2049">
        {/* NoScript fallback pixels per LeadID + TrustedForm docs */}
        <img src="//create.leadid.com/noscript.gif?lac=ceb2718e-dd95-93d4-34f3-0b88202d403a&lck=d50a3557-9854-7b8f-792c-527750401f5f&snippet_version=2" alt="" data-id="element-2050" />
        <img src="http://api.trustedform.com/ns.gif" alt="" data-id="element-2051" />
      </noscript>
    </div>;
}
/**
 * Read the current LeadID + TrustedForm values from the DOM at submit time.
 * Safe to call even if the scripts haven't loaded — returns empty strings.
 */
export function readLeadTrackingValues(): {
  leadid_token: string;
  xxTrustedFormCertUrl: string;
} {
  if (typeof document === 'undefined') {
    return {
      leadid_token: '',
      xxTrustedFormCertUrl: ''
    };
  }
  // Pick the first NON-EMPTY input for each — TrustedForm injects its
  // input(s) inside <form> elements, and there may be more than one
  // matching node in the DOM. We want the populated one.
  const pickFirstNonEmpty = (name: string): string => {
    const nodes = document.querySelectorAll<HTMLInputElement>(`input[name="${name}"]`);
    for (let i = 0; i < nodes.length; i++) {
      const v = nodes[i].value;
      if (v) return v;
    }
    return '';
  };
  return {
    leadid_token: pickFirstNonEmpty('leadid_token'),
    xxTrustedFormCertUrl: pickFirstNonEmpty('xxTrustedFormCertUrl')
  };
}
/**
 * Read any URL query parameters captured on the visitor's first landing
 * page-view this session (first-touch). Lead form submit handlers should
 * merge the returned object into their outbound payload so subID/affiliate
 * tracking params (e.g. ?sub1=123&sub2=345) are forwarded to the lead API.
 */
export function readUrlTrackingParams(): Record<string, string> {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
    return {};
  }
  try {
    const raw = window.sessionStorage.getItem(URL_PARAMS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}